import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { generateSampleResponses } from '../utils/sampleDataGenerator';

const SurveyContext = createContext(null);

const STORAGE_KEY = 'researchWizard_surveys';

const getDefaultSurvey = () => ({
  id: uuidv4(),
  title: 'Untitled Survey',
  description: '',
  status: 'draft',
  createdAt: new Date().toISOString(),
  updatedAt: new Date().toISOString(),
  settings: {
    allowMultipleResponses: false,
    showProgressBar: true,
    shuffleQuestions: false,
    requiredByDefault: false
  },
  questions: [],
  responses: []
});

const questionTypeDefaults = {
  multipleChoice: {
    options: [
      { id: uuidv4(), text: 'Option 1' },
      { id: uuidv4(), text: 'Option 2' },
      { id: uuidv4(), text: 'Option 3' }
    ]
  },
  checkboxes: {
    options: [
      { id: uuidv4(), text: 'Option 1' },
      { id: uuidv4(), text: 'Option 2' },
      { id: uuidv4(), text: 'Option 3' }
    ]
  },
  dropdown: {
    options: [
      { id: uuidv4(), text: 'Option 1' },
      { id: uuidv4(), text: 'Option 2' },
      { id: uuidv4(), text: 'Option 3' }
    ]
  },
  linearScale: {
    scaleMin: 1,
    scaleMax: 5,
    minLabel: 'Not at all',
    maxLabel: 'Extremely'
  },
  likertScale: {
    points: 5,
    labels: ['Strongly Disagree', 'Disagree', 'Neutral', 'Agree', 'Strongly Agree']
  },
  shortAnswer: { maxLength: 200 },
  longAnswer: { maxLength: 2000 },
  yesNo: {},
  email: {},
  number: { min: null, max: null }
};

export const SurveyProvider = ({ children }) => {
  const [surveys, setSurveys] = useState([]);
  const [surveysLoaded, setSurveysLoaded] = useState(false);
  const [currentSurvey, setCurrentSurvey] = useState(null);
  const [selectedQuestionId, setSelectedQuestionId] = useState(null);
  const [previewMode, setPreviewMode] = useState(false);
  const [responseMode, setResponseMode] = useState(false);
  const [resultsMode, setResultsMode] = useState(false);
  const [currentResponseSurveyId, setCurrentResponseSurveyId] = useState(null);
  const [wizardIntegrationChoice, setWizardIntegrationChoice] = useState(null);

  useEffect(() => {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        setSurveys(parsed);
      } catch (e) {
        console.error('Failed to load surveys:', e);
      }
    }
    setSurveysLoaded(true);
  }, []);

  useEffect(() => {
    if (surveys.length > 0) {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(surveys));
    }
  }, [surveys]);

  const saveSurvey = useCallback((survey) => {
    const updated = { ...survey, updatedAt: new Date().toISOString() };
    setSurveys(prev => {
      const existing = prev.findIndex(s => s.id === survey.id);
      if (existing >= 0) {
        const newSurveys = [...prev];
        newSurveys[existing] = updated;
        return newSurveys;
      }
      return [...prev, updated];
    });
    setCurrentSurvey(updated);
    return updated;
  }, []);

  const createNewSurvey = useCallback((title = 'Untitled Survey') => {
    const newSurvey = { ...getDefaultSurvey(), title };
    setSurveys(prev => [...prev, newSurvey]);
    setCurrentSurvey(newSurvey);
    setSelectedQuestionId(null);
    return newSurvey;
  }, []);

  const loadSurvey = useCallback((surveyId) => {
    const survey = surveys.find(s => s.id === surveyId);
    if (survey) {
      setCurrentSurvey(survey);
      setSelectedQuestionId(null);
    }
    return survey;
  }, [surveys]);

  const deleteSurvey = useCallback((surveyId) => {
    setSurveys(prev => prev.filter(s => s.id !== surveyId));
    if (currentSurvey?.id === surveyId) {
      setCurrentSurvey(null);
    }
  }, [currentSurvey]);

  const addQuestion = useCallback((type) => {
    if (!currentSurvey) return null;
    
    const newQuestion = {
      id: uuidv4(),
      type,
      text: `New ${type.replace(/([A-Z])/g, ' $1').trim()} Question`,
      helpText: '',
      required: currentSurvey.settings.requiredByDefault,
      ...questionTypeDefaults[type]
    };

    const updated = {
      ...currentSurvey,
      questions: [...currentSurvey.questions, newQuestion]
    };
    saveSurvey(updated);
    setSelectedQuestionId(newQuestion.id);
    return newQuestion;
  }, [currentSurvey, saveSurvey]);

  const updateQuestion = useCallback((questionId, updates) => {
    if (!currentSurvey) return;
    
    const updated = {
      ...currentSurvey,
      questions: currentSurvey.questions.map(q =>
        q.id === questionId ? { ...q, ...updates } : q
      )
    };
    saveSurvey(updated);
  }, [currentSurvey, saveSurvey]);

  const deleteQuestion = useCallback((questionId) => {
    if (!currentSurvey) return;
    
    const updated = {
      ...currentSurvey,
      questions: currentSurvey.questions.filter(q => q.id !== questionId)
    };
    saveSurvey(updated);
    if (selectedQuestionId === questionId) {
      setSelectedQuestionId(null);
    }
  }, [currentSurvey, saveSurvey, selectedQuestionId]);

  const duplicateQuestion = useCallback((questionId) => {
    if (!currentSurvey) return;
    
    const question = currentSurvey.questions.find(q => q.id === questionId);
    if (!question) return;

    const duplicated = {
      ...JSON.parse(JSON.stringify(question)),
      id: uuidv4(),
      text: question.text + ' (Copy)'
    };

    if (duplicated.options) {
      duplicated.options = duplicated.options.map(opt => ({
        ...opt,
        id: uuidv4()
      }));
    }

    const index = currentSurvey.questions.findIndex(q => q.id === questionId);
    const newQuestions = [...currentSurvey.questions];
    newQuestions.splice(index + 1, 0, duplicated);

    const updated = { ...currentSurvey, questions: newQuestions };
    saveSurvey(updated);
    setSelectedQuestionId(duplicated.id);
  }, [currentSurvey, saveSurvey]);

  const reorderQuestions = useCallback((startIndex, endIndex) => {
    if (!currentSurvey) return;
    
    const newQuestions = [...currentSurvey.questions];
    const [removed] = newQuestions.splice(startIndex, 1);
    newQuestions.splice(endIndex, 0, removed);

    const updated = { ...currentSurvey, questions: newQuestions };
    saveSurvey(updated);
  }, [currentSurvey, saveSurvey]);

  const updateSurveySettings = useCallback((updates) => {
    if (!currentSurvey) return;
    const updated = { ...currentSurvey, ...updates };
    saveSurvey(updated);
  }, [currentSurvey, saveSurvey]);

  const publishSurvey = useCallback(() => {
    if (!currentSurvey) return;
    const updated = { ...currentSurvey, status: 'live' };
    saveSurvey(updated);
  }, [currentSurvey, saveSurvey]);

  const closeSurvey = useCallback(() => {
    if (!currentSurvey) return;
    const updated = { ...currentSurvey, status: 'closed' };
    saveSurvey(updated);
  }, [currentSurvey, saveSurvey]);

  const addResponse = useCallback((surveyId, answers, completionTime = 0) => {
    const survey = surveys.find(s => s.id === surveyId);
    if (!survey) return null;

    const response = {
      responseId: uuidv4(),
      submittedAt: new Date().toISOString(),
      answers,
      completionTime
    };

    const existingResponses = survey.responses || [];
    const updated = {
      ...survey,
      responses: [...existingResponses, response]
    };

    setSurveys(prev => prev.map(s => s.id === surveyId ? updated : s));
    if (currentSurvey?.id === surveyId) {
      setCurrentSurvey(updated);
    }
    return response;
  }, [surveys, currentSurvey]);

  const getSurveyForResponse = useCallback((surveyId) => {
    return surveys.find(s => s.id === surveyId && s.status === 'live');
  }, [surveys]);

  const addSampleResponses = useCallback((surveyId, count = 400) => {
    const survey = surveys.find(s => s.id === surveyId);
    if (!survey || survey.questions.length === 0) return null;

    const sampleResponses = generateSampleResponses(survey, count);
    const existingResponses = survey.responses || [];
    const updated = {
      ...survey,
      responses: [...existingResponses, ...sampleResponses]
    };

    setSurveys(prev => prev.map(s => s.id === surveyId ? updated : s));
    if (currentSurvey?.id === surveyId) {
      setCurrentSurvey(updated);
    }
    return sampleResponses.length;
  }, [surveys, currentSurvey]);

  const getSelectedQuestion = useCallback(() => {
    if (!currentSurvey || !selectedQuestionId) return null;
    return currentSurvey.questions.find(q => q.id === selectedQuestionId);
  }, [currentSurvey, selectedQuestionId]);

  const value = {
    surveys,
    surveysLoaded,
    currentSurvey,
    selectedQuestionId,
    previewMode,
    responseMode,
    resultsMode,
    currentResponseSurveyId,
    wizardIntegrationChoice,
    setWizardIntegrationChoice,
    setPreviewMode,
    setResponseMode,
    setResultsMode,
    setCurrentResponseSurveyId,
    setSelectedQuestionId,
    createNewSurvey,
    loadSurvey,
    deleteSurvey,
    saveSurvey,
    addQuestion,
    updateQuestion,
    deleteQuestion,
    duplicateQuestion,
    reorderQuestions,
    updateSurveySettings,
    publishSurvey,
    closeSurvey,
    addResponse,
    getSurveyForResponse,
    getSelectedQuestion,
    addSampleResponses
  };

  return (
    <SurveyContext.Provider value={value}>
      {children}
    </SurveyContext.Provider>
  );
};

export const useSurvey = () => {
  const context = useContext(SurveyContext);
  if (!context) {
    throw new Error('useSurvey must be used within a SurveyProvider');
  }
  return context;
};

export default SurveyContext;
